<template>
  <div :class="appShellClass" :data-locale="$i18n.locale">
    <aside
      v-if="!isAuthRoute"
      class="desktop-shell-sidebar hidden nav:flex"
      :class="{ 'desktop-shell-sidebar-expanded': isDesktopSidebarExpanded }"
      @mouseenter="setDesktopSidebarExpanded(true)"
      @mouseleave="setDesktopSidebarExpanded(false)"
      @focusin="setDesktopSidebarExpanded(true)"
      @focusout="handleDesktopSidebarFocusOut"
    >
      <div class="desktop-shell-rail">
        <div class="flex items-center gap-3 px-4 pt-4">
          <a
            class="flex min-w-0 items-center gap-3"
            :href="resolveAppHref(isAuthenticated ? '/feed' : '/')"
            @click.prevent="navigateWithReload(isAuthenticated ? '/feed' : '/')"
          >
            <div class="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-white/10 ring-1 ring-white/15">
              <img
                src="@/assets/logoDRVN.png"
                alt="DRVN logo"
                class="h-full w-full object-cover"
              />
            </div>
            <div class="desktop-sidebar-label min-w-0">
              <p class="font-display text-sm uppercase tracking-[0.35em] text-steel-300">DRVN</p>
              <p class="truncate text-xs text-steel-400">{{ $t('app.tagline') }}</p>
            </div>
          </a>
        </div>

        <div v-if="isAuthenticated" class="desktop-sidebar-expand px-4 pt-5">
          <form class="relative" data-search-root @submit.prevent="handleSearch">
            <input
              v-model.trim="searchQuery"
              type="text"
              :placeholder="$t('app.searchPlaceholder')"
              class="w-full rounded-2xl border border-white/10 bg-midnight-900/70 px-4 py-3 text-xs uppercase tracking-[0.24em] text-steel-200 placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              @focus="searchActive = true"
              @input="queueSearch"
            />
            <div
              v-if="showSearchResults"
              class="absolute left-0 right-0 top-14 z-20 rounded-2xl border border-white/10 bg-midnight-900/95 p-2 text-xs text-steel-200 shadow-glow backdrop-blur"
            >
              <button
                v-for="user in searchResults"
                :key="user.handle"
                type="button"
                class="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition hover:bg-white/5"
                @click="goToUser(user.handle)"
              >
                <div class="h-8 w-8 overflow-hidden rounded-full border border-white/10 bg-midnight-900/70">
                  <img
                    v-if="user.avatar"
                    :src="user.avatar"
                    alt="User avatar"
                    class="h-full w-full object-cover"
                    referrerpolicy="no-referrer"
                    crossorigin="anonymous"
                  />
                </div>
                <div class="min-w-0">
                  <div class="flex items-center gap-1.5">
                    <p class="truncate text-sm text-white">{{ user.fullName || 'DRVN Member' }}</p>
                    <verified-badge v-if="user.verified" size="xs" />
                    <photographer-badge v-if="isFeaturedPhotographer(user)" size="xs" :title="$t('home.featuredPhotographerBadge')" />
                  </div>
                  <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">@{{ user.handle }}</p>
                </div>
              </button>
              <p v-if="!searchResults.length && !searching" class="px-3 py-2 text-[10px] uppercase tracking-[0.3em] text-steel-400">
                {{ $t('app.noUserFound') }}
              </p>
              <p v-if="searching" class="px-3 py-2 text-[10px] uppercase tracking-[0.3em] text-steel-400">
                {{ $t('app.searching') }}
              </p>
            </div>
          </form>
        </div>

        <nav class="mt-5 flex flex-1 flex-col gap-2 px-3">
          <template v-if="isAuthenticated">
            <a :class="desktopSidebarLinkClass('/feed')" :href="resolveAppHref('/feed')" @click.prevent="navigateWithReload('/feed')">
              <svg class="h-5 w-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M3 12.5 12 4l9 8.5" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M6.5 10.5V20h11V10.5" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              <span class="desktop-sidebar-label">{{ $t('nav.feed') }}</span>
            </a>
            <a :class="desktopSidebarLinkClass('/highlights')" :href="resolveAppHref('/highlights')" @click.prevent="navigateWithReload('/highlights')">
              <svg class="h-5 w-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="m12 3 2.6 5.26 5.8.84-4.2 4.1.99 5.8L12 16.27 6.81 19l.99-5.8-4.2-4.1 5.8-.84L12 3Z" stroke-width="1.7" stroke-linejoin="round" />
              </svg>
              <span class="desktop-sidebar-label">Highlights</span>
            </a>
            <a :class="desktopSidebarLinkClass('/meets')" :href="resolveAppHref('/meets')" @click.prevent="navigateWithReload('/meets')">
              <svg class="h-5 w-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M8 3v3M16 3v3M4 9h16" stroke-width="1.8" stroke-linecap="round" />
                <rect x="4" y="5.5" width="16" height="14.5" rx="3" stroke-width="1.8" />
              </svg>
              <span class="desktop-sidebar-label">{{ $t('nav.meets') }}</span>
            </a>
            <a v-if="isDriveRadarEnabled" :class="desktopSidebarLinkClass('/radar')" :href="resolveAppHref('/radar')" @click.prevent="navigateWithReload('/radar')">
              <svg class="h-5 w-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="12" cy="12" r="7" stroke-width="1.8" />
                <circle cx="12" cy="12" r="2.2" stroke-width="1.8" />
                <path d="M12 2v3M12 19v3M2 12h3M19 12h3" stroke-width="1.8" stroke-linecap="round" />
              </svg>
              <span class="desktop-sidebar-label">{{ $t('nav.radar') }}</span>
            </a>
            <a v-if="isTournamentEnabled" :class="desktopSidebarLinkClass('/tournament')" :href="resolveAppHref('/tournament')" @click.prevent="navigateWithReload('/tournament')">
              <svg class="h-5 w-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M7 4h10v3a4 4 0 0 1-4 4h-2A4 4 0 0 1 7 7V4Z" stroke-width="1.8" stroke-linejoin="round" />
                <path d="M9 20h6M12 11v9" stroke-width="1.8" stroke-linecap="round" />
                <path d="M7 5H5a2 2 0 0 0 2 2M17 5h2a2 2 0 0 1-2 2" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              <span class="desktop-sidebar-label">{{ $t('nav.tournament') }}</span>
            </a>
            <a v-if="isStickersEnabled" :class="desktopSidebarLinkClass('/stickers')" :href="resolveAppHref('/stickers')" @click.prevent="navigateWithReload('/stickers')">
              <svg class="h-5 w-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M6.5 4h7.2L20 10.3v7.2A2.5 2.5 0 0 1 17.5 20h-11A2.5 2.5 0 0 1 4 17.5v-11A2.5 2.5 0 0 1 6.5 4Z" stroke-width="1.8" stroke-linejoin="round" />
                <path d="M13.5 4v4.5A1.5 1.5 0 0 0 15 10h5" stroke-width="1.8" stroke-linejoin="round" />
              </svg>
              <span class="desktop-sidebar-label">{{ $t('nav.stickers') }}</span>
            </a>
            <a v-if="isMessagesEnabled" :class="desktopSidebarLinkClass('/messages')" :href="resolveAppHref('/messages')" @click.prevent="navigateWithReload('/messages')">
              <span class="relative inline-flex shrink-0">
                <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M5 6.5h14a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H9l-4 3v-3H5a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2Z" stroke-width="1.8" stroke-linejoin="round" />
                </svg>
                <span
                  v-if="messageUnreadCount"
                  class="absolute -right-2 -top-2 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-neon-400 px-1 text-[9px] font-semibold text-midnight-900"
                >
                  {{ messageUnreadCount > 9 ? '9+' : messageUnreadCount }}
                </span>
              </span>
              <span class="desktop-sidebar-label">{{ $t('nav.messages') }}</span>
            </a>
          </template>

          <template v-else>
            <a :class="desktopSidebarLinkClass('/login')" :href="resolveAppHref('/login')" @click.prevent="navigateWithReload('/login')">
              <svg class="h-5 w-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M10 17l5-5-5-5" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M15 12H4" stroke-width="1.8" stroke-linecap="round" />
                <path d="M20 4v16" stroke-width="1.8" stroke-linecap="round" />
              </svg>
              <span class="desktop-sidebar-label">{{ $t('nav.login') }}</span>
            </a>
            <a :class="desktopSidebarLinkClass('/register')" :href="resolveAppHref('/register')" @click.prevent="navigateWithReload('/register')">
              <svg class="h-5 w-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M12 5v14M5 12h14" stroke-width="1.8" stroke-linecap="round" />
              </svg>
              <span class="desktop-sidebar-label">{{ $t('nav.register') }}</span>
            </a>
          </template>
        </nav>

        <div class="mt-auto flex flex-col gap-2 px-3 pb-4">
          <a v-if="isAuthenticated" :class="desktopSidebarLinkClass('/notifications')" :href="resolveAppHref('/notifications')" @click.prevent="openNotifications">
            <span class="relative inline-flex shrink-0">
              <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 1 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              <span
                v-if="unreadCount"
                class="absolute -right-2 -top-2 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-500 px-1 text-[9px] font-semibold text-white"
              >
                {{ unreadCount > 9 ? '9+' : unreadCount }}
              </span>
            </span>
            <span class="desktop-sidebar-label">{{ $t('nav.notifications') }}</span>
          </a>

          <button
            type="button"
            class="desktop-sidebar-link"
            :class="isLightTheme ? 'desktop-sidebar-link-active' : ''"
            :aria-label="isLightTheme ? $t('theme.switchToDark') : $t('theme.switchToLight')"
            :title="isLightTheme ? $t('theme.switchToDark') : $t('theme.switchToLight')"
            @click="toggleTheme"
          >
            <svg v-if="isLightTheme" class="h-5 w-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="12" cy="12" r="4" stroke-width="1.6" />
              <path d="M12 2.5v2.25M12 19.25v2.25M4.93 4.93l1.59 1.59M17.48 17.48l1.59 1.59M2.5 12h2.25M19.25 12h2.25M4.93 19.07l1.59-1.59M17.48 6.52l1.59-1.59" stroke-width="1.6" stroke-linecap="round" />
            </svg>
            <svg v-else class="h-5 w-5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            <span class="desktop-sidebar-label">{{ isLightTheme ? 'Light' : 'Dark' }}</span>
          </button>

          <div v-if="isAuthenticated" class="relative" data-account-menu-root>
            <button
              type="button"
              class="desktop-sidebar-link w-full"
              @click="accountMenuOpen = !accountMenuOpen"
              aria-haspopup="true"
              :aria-expanded="accountMenuOpen"
            >
              <span class="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-white/10 bg-midnight-900/70">
                <img
                  v-if="displayAvatarUrl"
                  :src="displayAvatarUrl"
                  alt="Profile avatar"
                  class="h-full w-full object-cover"
                  referrerpolicy="no-referrer"
                  crossorigin="anonymous"
                />
                <span v-else class="text-sm font-semibold text-steel-200">{{ initials }}</span>
              </span>
              <span class="desktop-sidebar-label flex min-w-0 flex-1 items-center justify-between gap-3">
                <span class="truncate text-left">{{ $t('nav.profile') }}</span>
                <svg class="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="m6 9 6 6 6-6" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </span>
            </button>

            <div v-if="accountMenuOpen" class="absolute bottom-0 left-[calc(100%+0.75rem)] w-52 rounded-2xl border border-white/10 bg-midnight-900/95 p-2 shadow-glow">
              <a class="block rounded-xl px-3 py-2 text-sm text-steel-200 hover:bg-white/5" :href="resolveAppHref(`/u/${publicProfileHandle}`)" @click.prevent="navigateWithReload(`/u/${publicProfileHandle}`)">{{ $t('nav.profile') }}</a>
              <a class="block rounded-xl px-3 py-2 text-sm text-steel-200 hover:bg-white/5" :href="resolveAppHref('/profile')" @click.prevent="navigateWithReload('/profile')">{{ $t('nav.settings') }}</a>
              <a class="block rounded-xl px-3 py-2 text-sm text-steel-200 hover:bg-white/5" :href="resolveAppHref('/member-card')" @click.prevent="navigateWithReload('/member-card')">{{ $t('nav.memberCard') }}</a>
              <a v-if="isStickersEnabled" class="block rounded-xl px-3 py-2 text-sm text-steel-200 hover:bg-white/5" :href="resolveAppHref('/stickers')" @click.prevent="navigateWithReload('/stickers')">{{ $t('nav.stickers') }}</a>
              <a v-if="isAdmin" class="block rounded-xl px-3 py-2 text-sm text-steel-200 hover:bg-white/5" :href="resolveAppHref('/admin')" @click.prevent="navigateWithReload('/admin')">{{ $t('nav.admin') }}</a>
              <a v-if="isAdmin" class="block rounded-xl px-3 py-2 text-sm text-steel-200 hover:bg-white/5" :href="resolveAppHref('/admin/sticker-orders')" @click.prevent="navigateWithReload('/admin/sticker-orders')">{{ $t('nav.stickerOrders') }}</a>
              <a class="block rounded-xl px-3 py-2 text-sm text-steel-200 hover:bg-white/5" :href="resolveAppHref('/report')" @click.prevent="navigateWithReload('/report')">{{ $t('nav.reportIssue') }}</a>
              <button class="block w-full rounded-xl px-3 py-2 text-left text-sm text-steel-200 hover:bg-white/5" @click="signOut">{{ $t('nav.logout') }}</button>
            </div>
          </div>
        </div>
      </div>
    </aside>

    <header v-if="showMobileHeader" data-app-header class="sticky top-0 z-[120] border-b border-white/5 bg-black py-2 nav:hidden">
      <div class="mx-auto flex w-full max-w-[88rem] flex-col gap-3 px-4 py-2 sm:px-8 lg:px-10 nav:flex-row nav:items-center nav:justify-between">
        <div class="flex items-center justify-between gap-3">
          <a class="hidden items-center gap-3 nav:flex" :href="resolveAppHref('/feed')" @click.prevent="navigateWithReload('/feed')">
            <div class="flex h-9 w-9 items-center justify-center overflow-hidden rounded-xl bg-white/10 ring-1 ring-white/15">
              <img
                src="@/assets/logoDRVN.png"
                alt="DRVN logo"
                class="h-full w-full object-cover"
              />
            </div>
            <div>
              <p class="font-display text-sm uppercase tracking-[0.35em] text-steel-300">DRVN</p>
              <p class="text-xs text-steel-400">{{ $t('app.tagline') }}</p>
            </div>
          </a>
          <button
            v-if="isAuthenticated"
            type="button"
            class="app-pressable inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white text-midnight-900 transition hover:bg-neon-400 nav:hidden"
            @click="openMobileComposer"
            aria-label="Create post"
          >
            <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M12 5v14M5 12h14" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
          <a v-else class="flex items-center gap-3 nav:hidden" :href="resolveAppHref('/feed')" @click.prevent="navigateWithReload('/feed')">
            <div class="flex h-9 w-9 items-center justify-center overflow-hidden rounded-xl bg-white/10 ring-1 ring-white/15">
              <img
                src="@/assets/logoDRVN.png"
                alt="DRVN logo"
                class="h-full w-full object-cover"
              />
            </div>
            <div>
              <p class="font-display text-sm uppercase tracking-[0.35em] text-steel-300">DRVN</p>
              <p class="text-xs text-steel-400">{{ $t('app.tagline') }}</p>
            </div>
          </a>
          <div class="flex items-center gap-2 nav:hidden">
            <button
              type="button"
              class="app-pressable inline-flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 p-2 text-steel-200 hover:text-white"
              @click="isNavOpen = !isNavOpen"
            >
              <svg v-if="!isNavOpen" class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M4 6h16M4 12h16M4 18h16" stroke-width="1.6" stroke-linecap="round" />
              </svg>
              <svg v-else class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M6 6l12 12M18 6l-12 12" stroke-width="1.6" stroke-linecap="round" />
              </svg>
            </button>
          </div>
        </div>

        <nav class="hidden flex-wrap items-center gap-3 text-xs uppercase tracking-[0.3em] text-steel-300 nav:flex">
          <button
            type="button"
            class="inline-flex items-center justify-center rounded-xl border border-white/10 bg-white/5 p-2 text-steel-200 transition hover:bg-white/10 hover:text-white"
            :aria-label="isLightTheme ? $t('theme.switchToDark') : $t('theme.switchToLight')"
            :title="isLightTheme ? $t('theme.switchToDark') : $t('theme.switchToLight')"
            @click="toggleTheme"
          >
            <svg v-if="isLightTheme" class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="12" cy="12" r="4" stroke-width="1.6" />
              <path d="M12 2.5v2.25M12 19.25v2.25M4.93 4.93l1.59 1.59M17.48 17.48l1.59 1.59M2.5 12h2.25M19.25 12h2.25M4.93 19.07l1.59-1.59M17.48 6.52l1.59-1.59" stroke-width="1.6" stroke-linecap="round" />
            </svg>
            <svg v-else class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
          <form class="relative" v-if="isAuthenticated" data-search-root @submit.prevent="handleSearch">
            <input
              v-model.trim="searchQuery"
              type="text"
              :placeholder="$t('app.searchPlaceholder')"
              class="w-44 rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-2 text-xs uppercase tracking-[0.3em] text-steel-200 placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30 sm:w-56"
              @focus="searchActive = true"
              @input="queueSearch"
            />
            <div
              v-if="showSearchResults"
              class="absolute left-0 top-11 z-20 w-64 rounded-2xl border border-white/10 bg-midnight-900/95 p-2 text-xs text-steel-200 shadow-glow backdrop-blur"
            >
              <button
                v-for="user in searchResults"
                :key="user.handle"
                type="button"
                class="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition hover:bg-white/5"
                @click="goToUser(user.handle)"
              >
                <div class="h-8 w-8 overflow-hidden rounded-full border border-white/10 bg-midnight-900/70">
                  <img
                    v-if="user.avatar"
                    :src="user.avatar"
                    alt="User avatar"
                    class="h-full w-full object-cover"
                    referrerpolicy="no-referrer"
                    crossorigin="anonymous"
                  />
                </div>
                <div>
                  <div class="flex items-center gap-1.5">
                    <p class="text-sm text-white">{{ user.fullName || 'DRVN Member' }}</p>
                    <verified-badge v-if="user.verified" size="xs" />
                    <photographer-badge v-if="isFeaturedPhotographer(user)" size="xs" :title="$t('home.featuredPhotographerBadge')" />
                  </div>
                  <p class="text-[10px] uppercase tracking-[0.3em] text-steel-400">@{{ user.handle }}</p>
                </div>
              </button>
              <p v-if="!searchResults.length && !searching" class="px-3 py-2 text-[10px] uppercase tracking-[0.3em] text-steel-400">
                {{ $t('app.noUserFound') }}
              </p>
              <p v-if="searching" class="px-3 py-2 text-[10px] uppercase tracking-[0.3em] text-steel-400">
                {{ $t('app.searching') }}
              </p>
            </div>
          </form>
          <a v-if="!isAuthenticated" :class="desktopNavLinkClass('/login')" :href="resolveAppHref('/login')" @click.prevent="navigateWithReload('/login')">{{ $t('nav.login') }}</a>
          <a v-if="!isAuthenticated" :class="desktopNavLinkClass('/register')" :href="resolveAppHref('/register')" @click.prevent="navigateWithReload('/register')">{{ $t('nav.register') }}</a>
          <div v-if="isAuthenticated" class="flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.03] p-1 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
            <a :class="desktopNavLinkClass('/feed')" :href="resolveAppHref('/feed')" @click.prevent="navigateWithReload('/feed')">
              <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M3 12.5 12 4l9 8.5" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M6.5 10.5V20h11V10.5" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              <span>{{ $t('nav.feed') }}</span>
            </a>
            <a :class="desktopNavLinkClass('/highlights')" :href="resolveAppHref('/highlights')" @click.prevent="navigateWithReload('/highlights')">
              <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="m12 3 2.6 5.26 5.8.84-4.2 4.1.99 5.8L12 16.27 6.81 19l.99-5.8-4.2-4.1 5.8-.84L12 3Z" stroke-width="1.7" stroke-linejoin="round" />
              </svg>
              <span>Highlights</span>
            </a>
            <a :class="desktopNavLinkClass('/meets')" :href="resolveAppHref('/meets')" @click.prevent="navigateWithReload('/meets')">
              <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M8 3v3M16 3v3M4 9h16" stroke-width="1.8" stroke-linecap="round" />
                <rect x="4" y="5.5" width="16" height="14.5" rx="3" stroke-width="1.8" />
              </svg>
              <span>{{ $t('nav.meets') }}</span>
            </a>
            <a v-if="isDriveRadarEnabled" :class="desktopNavLinkClass('/radar')" :href="resolveAppHref('/radar')" @click.prevent="navigateWithReload('/radar')">
              <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="12" cy="12" r="7" stroke-width="1.8" />
                <circle cx="12" cy="12" r="2.2" stroke-width="1.8" />
                <path d="M12 2v3M12 19v3M2 12h3M19 12h3" stroke-width="1.8" stroke-linecap="round" />
              </svg>
              <span>{{ $t('nav.radar') }}</span>
            </a>
            <a v-if="isTournamentEnabled" :class="desktopNavLinkClass('/tournament')" :href="resolveAppHref('/tournament')" @click.prevent="navigateWithReload('/tournament')">
              <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M7 4h10v3a4 4 0 0 1-4 4h-2A4 4 0 0 1 7 7V4Z" stroke-width="1.8" stroke-linejoin="round" />
                <path d="M9 20h6M12 11v9" stroke-width="1.8" stroke-linecap="round" />
                <path d="M7 5H5a2 2 0 0 0 2 2M17 5h2a2 2 0 0 1-2 2" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              <span>{{ $t('nav.tournament') }}</span>
            </a>
            <a v-if="isStickersEnabled" :class="desktopNavLinkClass('/stickers')" :href="resolveAppHref('/stickers')" @click.prevent="navigateWithReload('/stickers')">
              <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M6.5 4h7.2L20 10.3v7.2A2.5 2.5 0 0 1 17.5 20h-11A2.5 2.5 0 0 1 4 17.5v-11A2.5 2.5 0 0 1 6.5 4Z" stroke-width="1.8" stroke-linejoin="round" />
                <path d="M13.5 4v4.5A1.5 1.5 0 0 0 15 10h5" stroke-width="1.8" stroke-linejoin="round" />
              </svg>
              <span>{{ $t('nav.stickers') }}</span>
            </a>
            <a v-if="isMessagesEnabled" :class="desktopNavLinkClass('/messages')" :href="resolveAppHref('/messages')" @click.prevent="navigateWithReload('/messages')">
              <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M5 6.5h14a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H9l-4 3v-3H5a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2Z" stroke-width="1.8" stroke-linejoin="round" />
              </svg>
              <span>{{ $t('nav.messages') }}</span>
              <span
                v-if="messageUnreadCount"
                class="ml-1.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-neon-400 px-1 text-[10px] font-semibold text-midnight-900"
              >
                {{ messageUnreadCount }}
              </span>
            </a>
          </div>
          <a v-if="isAuthenticated" :class="iconActionClass('/notifications')" :href="resolveAppHref('/notifications')" @click.prevent="openNotifications">
            <span class="sr-only">{{ $t('nav.notifications') }}</span>
            <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 1 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            <span
              v-if="unreadCount"
              class="absolute -right-2 -top-2 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-semibold text-white transform transition-transform"
              :class="showTransientBadge ? 'pop-animate' : ''"
            >
              {{ unreadCount }}
            </span>
          </a>
          <div v-if="isAuthenticated" class="relative" data-account-menu-root>
            <button
              type="button"
              class="inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/[0.03] p-1 pr-3 text-steel-200 transition hover:border-white/20 hover:bg-white/10 hover:text-white"
              @click="accountMenuOpen = !accountMenuOpen"
              aria-haspopup="true"
              :aria-expanded="accountMenuOpen"
            >
              <div class="h-8 w-8 overflow-hidden rounded-full border border-white/10 bg-midnight-900/70">
              <img
                v-if="displayAvatarUrl"
                :src="displayAvatarUrl"
                alt="Profile avatar"
                class="h-full w-full object-cover"
                referrerpolicy="no-referrer"
                crossorigin="anonymous"
              />
                <div v-else class="flex h-full w-full items-center justify-center text-sm font-semibold text-steel-200">{{ initials }}</div>
              </div>
              <span class="text-[10px] font-semibold uppercase tracking-[0.22em]">{{ $t('nav.profile') }}</span>
            </button>

            <div v-if="accountMenuOpen" class="absolute right-0 mt-2 w-44 rounded-2xl border border-white/10 bg-midnight-900/95 p-2 shadow-glow">
              <a class="block rounded-xl px-3 py-2 text-sm text-steel-200 hover:bg-white/5" :href="resolveAppHref(`/u/${publicProfileHandle}`)" @click.prevent="navigateWithReload(`/u/${publicProfileHandle}`)">{{ $t('nav.profile') }}</a>
              <a class="block rounded-xl px-3 py-2 text-sm text-steel-200 hover:bg-white/5" :href="resolveAppHref('/profile')" @click.prevent="navigateWithReload('/profile')">{{ $t('nav.settings') }}</a>
              <a class="block rounded-xl px-3 py-2 text-sm text-steel-200 hover:bg-white/5" :href="resolveAppHref('/member-card')" @click.prevent="navigateWithReload('/member-card')">{{ $t('nav.memberCard') }}</a>
              <a v-if="isStickersEnabled" class="block rounded-xl px-3 py-2 text-sm text-steel-200 hover:bg-white/5" :href="resolveAppHref('/stickers')" @click.prevent="navigateWithReload('/stickers')">{{ $t('nav.stickers') }}</a>
              <a v-if="isAdmin" class="block rounded-xl px-3 py-2 text-sm text-steel-200 hover:bg-white/5" :href="resolveAppHref('/admin')" @click.prevent="navigateWithReload('/admin')">{{ $t('nav.admin') }}</a>
              <a v-if="isAdmin" class="block rounded-xl px-3 py-2 text-sm text-steel-200 hover:bg-white/5" :href="resolveAppHref('/admin/sticker-orders')" @click.prevent="navigateWithReload('/admin/sticker-orders')">{{ $t('nav.stickerOrders') }}</a>
              <a class="block rounded-xl px-3 py-2 text-sm text-steel-200 hover:bg-white/5" :href="resolveAppHref('/report')" @click.prevent="navigateWithReload('/report')">{{ $t('nav.reportIssue') }}</a>
              <button class="block w-full text-left rounded-xl px-3 py-2 text-sm text-steel-200 hover:bg-white/5" @click="signOut">{{ $t('nav.logout') }}</button>
            </div>
          </div> 
        </nav>
      </div>
      <nav
        v-if="isNavOpen"
        class="mobile-nav-scroll relative z-[130] flex max-h-[calc(100svh-7.5rem)] flex-col gap-4 overflow-y-scroll overscroll-contain border-t border-white/5 px-6 pb-5 text-xs uppercase tracking-[0.3em] text-steel-300 nav:hidden"
      >
        <button
          type="button"
          class="mt-4 inline-flex items-center justify-center rounded-xl border border-white/10 bg-white/5 p-2 text-steel-200 transition hover:bg-white/10 hover:text-white"
          :aria-label="isLightTheme ? $t('theme.switchToDark') : $t('theme.switchToLight')"
          :title="isLightTheme ? $t('theme.switchToDark') : $t('theme.switchToLight')"
          @click="toggleTheme"
        >
          <svg v-if="isLightTheme" class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <circle cx="12" cy="12" r="4" stroke-width="1.6" />
            <path d="M12 2.5v2.25M12 19.25v2.25M4.93 4.93l1.59 1.59M17.48 17.48l1.59 1.59M2.5 12h2.25M19.25 12h2.25M4.93 19.07l1.59-1.59M17.48 6.52l1.59-1.59" stroke-width="1.6" stroke-linecap="round" />
          </svg>
          <svg v-else class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <div v-if="!isAuthenticated" class="grid w-full gap-3 pt-2">
          <a :class="mobileNavLinkClass('/login')" :href="resolveAppHref('/login')" @click.prevent="navigateWithReload('/login')">{{ $t('nav.login') }}</a>
          <a :class="mobileNavLinkClass('/register')" :href="resolveAppHref('/register')" @click.prevent="navigateWithReload('/register')">{{ $t('nav.register') }}</a>
        </div>
        <div v-if="isAuthenticated" class="grid w-full gap-3 pt-2">
          <a :class="mobileNavLinkClass('/highlights')" :href="resolveAppHref('/highlights')" @click.prevent="navigateWithReload('/highlights')">
            <span>Highlights</span>
            <span class="text-[10px] tracking-[0.2em] text-steel-400">top 3 + weekly build</span>
          </a>
          <a v-if="isDriveRadarEnabled" :class="mobileNavLinkClass('/radar')" :href="resolveAppHref('/radar')" @click.prevent="navigateWithReload('/radar')">
            <span>{{ $t('nav.radar') }}</span>
            <span class="text-[10px] tracking-[0.2em] text-steel-400">live map</span>
          </a>
          <a v-if="isTournamentEnabled" :class="mobileNavLinkClass('/tournament')" :href="resolveAppHref('/tournament')" @click.prevent="navigateWithReload('/tournament')">
            <span>{{ $t('nav.tournament') }}</span>
            <span class="text-[10px] tracking-[0.2em] text-steel-400">competition</span>
          </a>
          <a v-if="isStickersEnabled" :class="mobileNavLinkClass('/stickers')" :href="resolveAppHref('/stickers')" @click.prevent="navigateWithReload('/stickers')">
            <span>{{ $t('nav.stickers') }}</span>
            <span class="text-[10px] tracking-[0.2em] text-steel-400">orders</span>
          </a>
          <a :class="mobileNavLinkClass('/notifications')" :href="resolveAppHref('/notifications')" @click.prevent="openNotifications">
            <span>{{ $t('nav.notifications') }}</span>
            <span
              v-if="unreadCount"
              class="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-semibold text-white transform transition-transform"
              :class="showTransientBadge ? 'pop-animate' : ''"
            >
              {{ unreadCount }}
            </span>
          </a>
          <a :class="mobileNavLinkClass('/profile')" :href="resolveAppHref('/profile')" @click.prevent="navigateWithReload('/profile')">
            <span>{{ $t('nav.settings') }}</span>
            <span class="text-[10px] tracking-[0.2em] text-steel-400">account</span>
          </a>
          <a :class="mobileNavLinkClass('/member-card')" :href="resolveAppHref('/member-card')" @click.prevent="navigateWithReload('/member-card')">
            <span>{{ $t('nav.memberCard') }}</span>
            <span class="text-[10px] tracking-[0.2em] text-steel-400">share</span>
          </a>
          <a v-if="isAdmin" :class="mobileNavLinkClass('/admin')" :href="resolveAppHref('/admin')" @click.prevent="navigateWithReload('/admin')">
            <span>{{ $t('nav.admin') }}</span>
            <span class="text-[10px] tracking-[0.2em] text-steel-400">control</span>
          </a>
          <a v-if="isAdmin" :class="mobileNavLinkClass('/admin/sticker-orders')" :href="resolveAppHref('/admin/sticker-orders')" @click.prevent="navigateWithReload('/admin/sticker-orders')">
            <span>{{ $t('nav.stickerOrders') }}</span>
            <span class="text-[10px] tracking-[0.2em] text-steel-400">shop</span>
          </a>
          <a :class="mobileNavLinkClass('/report')" :href="resolveAppHref('/report')" @click.prevent="navigateWithReload('/report')">
            <span>{{ $t('nav.reportIssue') }}</span>
            <span class="text-[10px] tracking-[0.2em] text-steel-400">support</span>
          </a>
          <button
            type="button"
            class="flex w-full items-center justify-center rounded-2xl border border-rose-500/20 bg-rose-500/10 px-4 py-3 text-sm font-semibold text-rose-200 transition hover:border-rose-400/30 hover:bg-rose-500/15 hover:text-white"
            @click="signOut"
          >
            {{ $t('nav.logout') }}
          </button>
        </div>
      </nav>
    </header>

    <header v-else-if="showMobileImmersiveHeader" class="sticky top-0 z-[120] border-b border-white/5 bg-black nav:hidden">
      <div class="mx-auto flex w-full max-w-[88rem] items-center justify-between gap-3 px-4 py-3 sm:px-8 lg:px-10">
        <button
          type="button"
          class="app-pressable inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white transition hover:bg-white/10"
          aria-label="Go back"
          @click="handleMobileImmersiveBack"
        >
          <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="m15 6-6 6 6 6" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <div class="min-w-0 flex-1 text-center">
          <p class="truncate text-sm font-semibold tracking-[0.08em] text-white">{{ mobileImmersiveHeaderTitle }}</p>
        </div>
        <div class="h-10 w-10 shrink-0" aria-hidden="true"></div>
      </div>
    </header>

    <main :class="mainShellClass">
      <router-view />
    </main>

    <teleport to="body">
      <div
        v-if="mobileSearchModalOpen"
        class="fixed inset-0 z-[160] bg-black/72 backdrop-blur-sm nav:hidden"
        @click.self="closeMobileSearch"
      >
        <section class="flex h-full flex-col bg-[linear-gradient(180deg,rgba(17,23,34,0.98),rgba(10,14,22,1))] pb-[calc(env(safe-area-inset-bottom,0px)+1.25rem)]">
          <div class="sticky top-0 z-10 border-b border-white/5 bg-black px-4 py-3">
            <div class="mx-auto flex w-full max-w-[88rem] items-center justify-between gap-3">
              <button
                type="button"
                class="app-pressable inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white transition hover:bg-white/10"
                aria-label="Go back"
                @click="closeMobileSearch"
              >
                <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="m15 6-6 6 6 6" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </button>
              <div class="min-w-0 flex-1 text-center">
                <p class="truncate text-sm font-semibold tracking-[0.08em] text-white">Search</p>
              </div>
              <div class="h-10 w-10 shrink-0" aria-hidden="true"></div>
            </div>
          </div>

          <form class="px-5 pt-5" @submit.prevent="handleSearch">
            <div class="relative">
              <input
                ref="mobileSearchModalInput"
                v-model.trim="searchQuery"
                type="text"
                :placeholder="$t('app.searchPlaceholder')"
                class="w-full rounded-[1.6rem] border border-white/10 bg-white/[0.04] px-5 py-4 pr-14 text-sm text-white placeholder:text-steel-400 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
                @focus="searchActive = true"
                @input="queueSearch"
              />
              <div class="pointer-events-none absolute inset-y-0 right-5 flex items-center text-steel-400">
                <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <circle cx="11" cy="11" r="6.5" stroke-width="1.8" />
                  <path d="m16 16 4.5 4.5" stroke-width="1.8" stroke-linecap="round" />
                </svg>
              </div>
            </div>
          </form>

          <div class="mt-5 flex-1 overflow-y-auto px-5">
            <div v-if="searchQuery" class="space-y-3">
              <button
                v-for="user in searchResults"
                :key="user.handle"
                type="button"
                class="flex w-full items-center gap-4 rounded-[1.5rem] border border-white/10 bg-white/[0.04] px-4 py-3 text-left transition hover:border-white/20 hover:bg-white/[0.07]"
                @click="goToUser(user.handle)"
              >
                <div class="h-12 w-12 overflow-hidden rounded-full border border-white/10 bg-midnight-900/70">
                  <img
                    v-if="user.avatar"
                    :src="user.avatar"
                    alt="User avatar"
                    class="h-full w-full object-cover"
                    referrerpolicy="no-referrer"
                    crossorigin="anonymous"
                  />
                  <div v-else class="flex h-full w-full items-center justify-center text-sm font-semibold text-steel-200">
                    {{ (user.fullName || 'D').split(' ').filter(Boolean).slice(0, 2).map((chunk) => chunk[0].toUpperCase()).join('') }}
                  </div>
                </div>
                <div class="min-w-0 flex-1">
                  <div class="flex items-center gap-1.5">
                    <p class="truncate text-sm font-semibold text-white">{{ user.fullName || 'DRVN Member' }}</p>
                    <verified-badge v-if="user.verified" size="xs" />
                    <photographer-badge v-if="isFeaturedPhotographer(user)" size="xs" :title="$t('home.featuredPhotographerBadge')" />
                  </div>
                  <p class="mt-1 text-[11px] uppercase tracking-[0.22em] text-steel-400">@{{ user.handle }}</p>
                </div>
              </button>
              <p v-if="!searchResults.length && !searching" class="rounded-[1.5rem] border border-dashed border-white/10 px-4 py-5 text-sm text-steel-400">
                {{ $t('app.noUserFound') }}
              </p>
              <p v-if="searching" class="rounded-[1.5rem] border border-white/10 px-4 py-5 text-sm text-steel-400">
                {{ $t('app.searching') }}
              </p>
            </div>
            <div v-else class="rounded-[1.75rem] border border-white/10 bg-white/[0.03] p-5">
              <p class="text-[10px] uppercase tracking-[0.28em] text-steel-400">Quick tip</p>
              <p class="mt-3 text-sm leading-6 text-steel-300">Search works on handles and full names. Try `@username`, first name, or full build owner name.</p>
            </div>
          </div>
        </section>
      </div>
    </teleport>

    <nav
      v-if="showMobileTabbar"
      data-mobile-tabbar
      class="fixed inset-x-0 bottom-0 z-[60] px-3 pb-[calc(env(safe-area-inset-bottom,0px)+0.75rem)] pt-2 nav:hidden"
    >
      <div class="mx-auto grid max-w-sm grid-cols-5 gap-1 rounded-[1.75rem] border border-white/10 bg-[linear-gradient(180deg,rgba(23,28,39,0.96),rgba(14,18,28,0.98))] p-2 shadow-[0_-12px_40px_rgba(0,0,0,0.38)] backdrop-blur-xl">
        <a :class="mobileTabbarLinkClass('/feed')" :href="resolveAppHref('/feed')" @click.prevent="navigateWithReload('/feed')">
          <span class="sr-only">{{ $t('nav.feed') }}</span>
          <svg class="h-[1.1rem] w-[1.1rem]" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M3 12.5 12 4l9 8.5" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M6.5 10.5V20h11V10.5" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </a>
        <button type="button" :class="mobileTabbarButtonClass(mobileSearchModalOpen)" @click="openMobileSearch">
          <span class="sr-only">Search</span>
          <svg class="h-[1.1rem] w-[1.1rem]" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <circle cx="11" cy="11" r="6.5" stroke-width="1.8" />
            <path d="m16 16 4.5 4.5" stroke-width="1.8" stroke-linecap="round" />
          </svg>
        </button>
        <a :class="mobileTabbarLinkClass('/meets')" :href="resolveAppHref('/meets')" @click.prevent="navigateWithReload('/meets')">
          <span class="sr-only">{{ $t('nav.meets') }}</span>
          <svg class="h-[1.1rem] w-[1.1rem]" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M8 3v3M16 3v3M4 9h16" stroke-width="1.8" stroke-linecap="round" />
            <rect x="4" y="5.5" width="16" height="14.5" rx="3" stroke-width="1.8" />
          </svg>
        </a>
        <a v-if="isMessagesEnabled" :class="mobileTabbarLinkClass('/messages')" :href="resolveAppHref('/messages')" @click.prevent="navigateWithReload('/messages')">
          <span class="sr-only">{{ $t('nav.messages') }}</span>
          <span class="relative inline-flex">
            <svg class="h-[1.1rem] w-[1.1rem]" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M5 6.5h14a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H9l-4 3v-3H5a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2Z" stroke-width="1.8" stroke-linejoin="round" />
            </svg>
            <span
              v-if="messageUnreadCount"
              class="absolute -right-2 -top-2 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-neon-400 px-1 text-[9px] font-semibold text-midnight-900"
            >
              {{ messageUnreadCount > 9 ? '9+' : messageUnreadCount }}
            </span>
          </span>
        </a>
        <a :class="mobileTabbarLinkClass(`/u/${publicProfileHandle}`)" :href="resolveAppHref(`/u/${publicProfileHandle}`)" @click.prevent="navigateWithReload(`/u/${publicProfileHandle}`)">
          <span class="sr-only">{{ $t('nav.profile') }}</span>
          <span class="flex h-7 w-7 items-center justify-center overflow-hidden rounded-full border border-current/20 bg-midnight-900/70">
            <img
              v-if="displayAvatarUrl"
              :src="displayAvatarUrl"
              alt="Profile avatar"
              class="h-full w-full object-cover"
              referrerpolicy="no-referrer"
              crossorigin="anonymous"
            />
            <span v-else class="text-[11px] font-semibold uppercase">{{ initials }}</span>
          </span>
        </a>
      </div>
    </nav>

    <cookie-consent />
    <div
      v-if="showPushBanner"
      class="fixed inset-x-4 bottom-24 z-[135] rounded-[1.75rem] border border-white/10 bg-midnight-900/95 p-4 shadow-glow backdrop-blur sm:inset-x-auto sm:bottom-6 sm:right-6 sm:w-[25rem]"
    >
      <div class="flex items-start gap-3">
        <div class="mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-neon-400/30 bg-neon-400/10 text-neon-300">
          <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M15 17h5l-1.4-1.4A2 2 0 0 1 18 14.2V11a6 6 0 1 0-12 0v3.2a2 2 0 0 1-.6 1.4L4 17h5" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M9.5 18.5a2.5 2.5 0 0 0 5 0" stroke-width="1.8" stroke-linecap="round" />
          </svg>
        </div>
        <div class="min-w-0 flex-1">
          <p class="font-display text-xs uppercase tracking-[0.3em] text-neon-400">{{ $t('app.pushBannerEyebrow') }}</p>
          <p class="mt-2 text-sm font-semibold text-white">{{ $t('app.pushBannerTitle') }}</p>
          <p class="mt-2 text-sm text-steel-300">{{ pushBannerMessage }}</p>
          <p v-if="pushBannerError" class="mt-2 text-sm text-rose-200">{{ pushBannerError }}</p>
          <div class="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              class="rounded-2xl bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.28em] text-midnight-900 transition hover:bg-neon-400 disabled:cursor-not-allowed disabled:opacity-70"
              :disabled="pushPromptBusy"
              @click="enablePushFromBanner"
            >
              {{ pushPromptBusy ? $t('app.pushBannerEnabling') : $t('app.pushBannerEnable') }}
            </button>
            <button
              type="button"
              class="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.28em] text-white transition hover:bg-white/10"
              @click="dismissPushBanner"
            >
              {{ $t('app.pushBannerDismiss') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <app-toast-stack :toasts="toasts" @dismiss="dismissToast" />

  </div>
</template>

<script>
import { supabase, getAvatarUrl, getReadySession, refreshReadySession, runSupabaseQueryWithRetry } from '@/lib/supabase'
import { syncDriveRadarLiveLocation } from '@/lib/driveRadarLiveLocation'
import { APP_TOAST_EVENT, emitAppToast } from '@/lib/toast'
import { getPushPermissionState, hasPushSubscription, isPushSupported, requestPushPermissionAndSubscribe, syncPushSubscription } from '@/lib/push'
import AppToastStack from '@/components/AppToastStack.vue'
import CookieConsent from '@/components/CookieConsent.vue'
import PhotographerBadge from '@/components/PhotographerBadge.vue'
import VerifiedBadge from '@/components/VerifiedBadge.vue'
import { isFeaturedPhotographerProfile } from '@/lib/specialProfiles'
import { getCachedFeatureFlags, loadFeatureFlags, onFeatureFlagsUpdated } from '@/lib/features'

export default {
  name: 'App',
  components: {
    AppToastStack,
    CookieConsent,
    PhotographerBadge,
    VerifiedBadge,
  },
  data() {
    return {
      session: null,
      unreadCount: 0,
      messageUnreadCount: 0,
      searchQuery: '',
      searchResults: [],
      searching: false,
      searchActive: false,
      mobileSearchModalOpen: false,
      searchTimer: null,
      isNavOpen: false,
      isDesktopSidebarExpanded: false,
      accountMenuOpen: false,
      profile: {},
      showTransientBadge: false,
      transientBadgeTimer: null,
      installPromptEvent: null,
      canInstallPwa: false,
      showInstallGuide: false,
      isMobileDevice: false,
      profileUpdateHandler: null,
      isLightTheme: false,
      messageChannel: null,
      notificationChannel: null,
      authSubscription: null,
      standaloneDisplayModeMedia: null,
      standaloneChangeHandler: null,
      beforeInstallPromptHandler: null,
      appInstalledHandler: null,
      serviceWorkerMessageHandler: null,
      dismissedInstallBanner: false,
      toasts: [],
      toastListener: null,
      resumeHandler: null,
      isRecoveringAfterResume: false,
      pushPromptBusy: false,
      pushPromptDismissed: false,
      pushBannerError: '',
      pushPermissionState: 'default',
      hasGrantedPushSubscription: false,
      featureFlags: getCachedFeatureFlags(),
      featureFlagsListenerCleanup: null,
    }
  },
  computed: {
    appShellClass() {
      return [
        'min-h-screen bg-midnight-900 text-steel-100 transition-colors duration-300',
        this.isLightTheme ? 'theme-light' : 'theme-dark',
      ]
    },
    mainShellClass() {
      return [
        this.showMobileTabbar ? 'pb-28 sm:pb-0' : '',
        this.isMobileImmersiveRoute ? 'min-h-[100svh]' : '',
        !this.isAuthRoute ? 'nav:pl-[6.5rem]' : '',
      ]
    },
    isAuthRoute() {
      return Boolean(this.$route?.meta?.auth)
    },
    isAuthenticated() {
      return Boolean(this.session?.user)
    },
    currentRoutePath() {
      return this.$route?.path || '/'
    },
    isAdmin() {
      return this.profile?.role === 'admin'
    },
    isDriveRadarEnabled() {
      return this.featureFlags.drive_radar_enabled !== false
    },
    isMessagesEnabled() {
      return this.featureFlags.messages_enabled !== false
    },
    isStickersEnabled() {
      return this.featureFlags.stickers_enabled !== false
    },
    isTournamentEnabled() {
      return this.featureFlags.tournament_enabled !== false
    },
    showSearchResults() {
      return Boolean(this.searchActive && this.searchQuery)
    },
    initials() {
      const full = this.profile?.full_name || this.session?.user?.user_metadata?.full_name || ''
      if (!full) return 'D'
      return full
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((s) => s[0].toUpperCase())
        .join('')
    },
    displayAvatarUrl() {
      const avatarPath = this.profile?.avatar_path || this.session?.user?.user_metadata?.avatar_path || ''
      const avatarUrl = this.profile?.avatar_url || this.session?.user?.user_metadata?.avatar_url || ''
      return getAvatarUrl({ avatarPath, avatarUrl })
    },
    publicProfileHandle() {
      const handle = this.profile?.handle || this.session?.user?.user_metadata?.handle || ''
      if (handle) return handle
      const userId = this.session?.user?.id || ''
      return userId ? userId.slice(0, 8) : 'member'
    },
    isIosDevice() {
      if (typeof navigator === 'undefined') return false
      return /iPhone|iPad|iPod/i.test(navigator.userAgent || '')
    },
    isAndroidDevice() {
      if (typeof navigator === 'undefined') return false
      return /Android/i.test(navigator.userAgent || '')
    },
    isRunningStandalone() {
      if (typeof window === 'undefined') return false
      const standaloneMedia = window.matchMedia?.('(display-mode: standalone)').matches
      const standaloneNavigator = window.navigator?.standalone === true
      return Boolean(standaloneMedia || standaloneNavigator)
    },
    installBannerTitle() {
      if (this.isIosDevice) return this.$t('install.iosTitle')
      if (this.isAndroidDevice) return this.$t('install.androidTitle')
      return this.$t('install.title')
    },
    installBannerMessage() {
      if (this.isIosDevice) {
        return this.$t('install.iosMessage')
      }
      if (this.isAndroidDevice) {
        return this.canInstallPwa
          ? this.$t('install.androidMessageReady')
          : this.$t('install.androidMessageFallback')
      }
      return this.$t('install.message')
    },
    installBannerHint() {
      if (this.isIosDevice) return this.$t('install.iosHint')
      if (this.isAndroidDevice) {
        return this.canInstallPwa
          ? this.$t('install.androidHintReady')
          : this.$t('install.androidHintFallback')
      }
      return this.$t('install.hint')
    },
    installBenefits() {
      return [
        this.$t('install.benefitFast'),
        this.$t('install.benefitFullscreen'),
        this.$t('install.benefitHome'),
      ]
    },
    installGuideButtonLabel() {
      if (this.isIosDevice) return this.$t('install.showIosSteps')
      return this.$t('install.showSteps')
    },
    installGuideTitle() {
      if (this.isIosDevice) return this.$t('install.iosTitle')
      if (this.isAndroidDevice) return this.$t('install.androidTitle')
      return this.$t('install.title')
    },
    installGuideHeading() {
      if (this.isIosDevice) return this.$t('install.iosGuideHeading')
      if (this.isAndroidDevice) return this.$t('install.androidGuideHeading')
      return this.$t('install.desktopGuideHeading')
    },
    installGuideDescription() {
      if (this.isIosDevice) return this.$t('install.iosGuideDescription')
      if (this.isAndroidDevice) return this.$t('install.androidGuideDescription')
      return this.$t('install.desktopGuideDescription')
    },
    installGuideSteps() {
      if (this.isIosDevice) {
        return [
          this.$t('install.iosStep1'),
          this.$t('install.iosStep2'),
          this.$t('install.iosStep3'),
          this.$t('install.iosStep4'),
        ]
      }

      if (this.isAndroidDevice) {
        return this.canInstallPwa
          ? [this.$t('install.androidStepReady1'), this.$t('install.androidStepReady2')]
          : [this.$t('install.androidStepFallback1'), this.$t('install.androidStepFallback2'), this.$t('install.androidStepFallback3')]
      }

      return [
        this.$t('install.desktopStep1'),
        this.$t('install.desktopStep2'),
      ]
    },
    showInstallBanner() {
      if (this.dismissedInstallBanner || this.isRunningStandalone) return false
      if (this.isIosDevice) return true
      if (this.isAndroidDevice) return true
      return this.canInstallPwa
    },
    showMobileTabbar() {
      return Boolean(this.isAuthenticated && !this.isAuthRoute && !this.isDirectMessageThreadRoute && !this.isMobileImmersiveRoute)
    },
    isDirectMessageThreadRoute() {
      return this.$route?.name === 'messages' && Boolean(this.$route?.params?.handle)
    },
    isMobileImmersiveRoute() {
      const routeName = this.$route?.name || ''
      if (routeName === 'notifications' || routeName === 'profile' || routeName === 'community-highlights') return true
      if (routeName === 'messages') return true
      if (routeName === 'feed' && this.$route?.query?.compose === '1') return true
      return false
    },
    showMobileHeader() {
      return Boolean(!this.isAuthRoute && !this.isMobileImmersiveRoute)
    },
    showMobileImmersiveHeader() {
      return Boolean(!this.isAuthRoute && this.isMobileImmersiveRoute && !this.isFeedComposerRoute)
    },
    isFeedComposerRoute() {
      return this.$route?.name === 'feed' && this.$route?.query?.compose === '1'
    },
    mobileImmersiveHeaderTitle() {
      const routeName = this.$route?.name || ''
      if (routeName === 'messages' && this.$route?.params?.handle) return 'Conversation'
      if (routeName === 'messages') return this.$t('nav.messages')
      if (routeName === 'notifications') return this.$t('nav.notifications')
      if (routeName === 'profile') return this.$t('nav.settings')
      if (routeName === 'community-highlights') return 'Highlights'
      return 'Back'
    },
    showPushBanner() {
      if (!this.isAuthenticated || this.isAuthRoute) return false
      if (!isPushSupported()) return false
      if (this.pushPromptDismissed) return false
      if (this.pushPromptBusy) return true
      return this.pushBannerEligible
    },
    pushBannerEligible() {
      return Boolean(this.session?.user && !this.hasGrantedPushSubscription && this.pushPermissionState !== 'denied')
    },
    pushBannerMessage() {
      if (this.pushPermissionState === 'granted') {
        return this.$t('app.pushBannerMessageGranted')
      }
      return this.$t('app.pushBannerMessageDefault')
    },
  },
  watch: {
    $route() {
      this.accountMenuOpen = false
      this.isNavOpen = false
      this.searchActive = false
      this.mobileSearchModalOpen = false
      this.searchQuery = ''
      this.searchResults = []
      this.refreshPushPromptState()
      this.applyRouteSeo()
    },
  },
  async mounted() {
    this.loadThemePreference()
    if (typeof window !== 'undefined') {
      this.dismissedInstallBanner = window.localStorage.getItem('drvn-dismissed-install-banner') === '1'
      this.pushPromptDismissed = window.localStorage.getItem('drvn-push-banner-dismissed') === '1'
    }
    this.applyRouteSeo()
    this.session = await getReadySession()
    try {
      this.featureFlags = await loadFeatureFlags()
    } catch (_error) {
      this.featureFlags = getCachedFeatureFlags()
    }
    this.featureFlagsListenerCleanup = onFeatureFlagsUpdated((flags) => {
      this.featureFlags = flags
      this.syncDriveRadarBackgroundTracking()
    })
    if (this.session?.user?.id) {
      // Load above-the-fold account data in parallel and defer secondary boot work.
      await Promise.allSettled([
        this.ensureProfile(this.session).then(() => this.fetchProfile()),
        this.refreshNotificationCount(),
        this.refreshMessageUnreadCount(),
      ])
      this.subscribeMessageUnreadCount()
      this.subscribeNotificationActivity()
      this.initializePushNotifications()
      this.syncDriveRadarBackgroundTracking()
    }
    this.refreshPushPromptState()

    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      this.session = session
      if (session?.user?.id) {
        await Promise.allSettled([
          this.ensureProfile(session).then(() => this.fetchProfile()),
          this.refreshNotificationCount(),
          this.refreshMessageUnreadCount(),
        ])
        this.subscribeMessageUnreadCount()
        this.subscribeNotificationActivity()
        this.initializePushNotifications()
        this.syncDriveRadarBackgroundTracking()
        this.pushPromptDismissed = false
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem('drvn-push-banner-dismissed')
        }
      } else {
        this.unreadCount = 0
        this.messageUnreadCount = 0
        this.profile = {}
        if (this.messageChannel) {
          supabase.removeChannel(this.messageChannel)
          this.messageChannel = null
        }
        this.teardownNotificationActivity()
        syncDriveRadarLiveLocation({ isActive: false })
      }
      this.refreshPushPromptState()
      if (event === 'SIGNED_OUT' && this.$route?.meta?.requiresAuth) {
        this.$router.replace('/login')
      }
    })
    this.authSubscription = authListener?.subscription || null

    const ua = navigator.userAgent || ''
    this.isMobileDevice = /Android|iPhone|iPad|iPod/i.test(ua)

    this.standaloneDisplayModeMedia = window.matchMedia ? window.matchMedia('(display-mode: standalone)') : null
    this.standaloneChangeHandler = () => this.handleStandaloneChange()
    if (this.standaloneDisplayModeMedia?.addEventListener) {
      this.standaloneDisplayModeMedia.addEventListener('change', this.standaloneChangeHandler)
    } else if (this.standaloneDisplayModeMedia?.addListener) {
      this.standaloneDisplayModeMedia.addListener(this.standaloneChangeHandler)
    }

    this.beforeInstallPromptHandler = (event) => {
      event.preventDefault()
      this.installPromptEvent = event
      this.canInstallPwa = true
      window.__drvnInstallPrompt = event
    }
    window.addEventListener('beforeinstallprompt', this.beforeInstallPromptHandler)

    this.appInstalledHandler = () => {
      this.installPromptEvent = null
      this.canInstallPwa = false
      window.__drvnInstallPrompt = null
      this.dismissInstallBanner()
    }
    window.addEventListener('appinstalled', this.appInstalledHandler)

    this.profileUpdateHandler = (event) => {
      const detail = event?.detail || {}
      if (detail.avatar_path || detail.avatar_url) {
        this.profile = {
          ...this.profile,
          avatar_path: detail.avatar_path ?? this.profile?.avatar_path,
          avatar_url: detail.avatar_url ?? this.profile?.avatar_url,
        }
        return
      }
      this.fetchProfile()
    }
    window.addEventListener('profile:updated', this.profileUpdateHandler)
    document.addEventListener('click', this.handleDocumentClick)

    this.toastListener = (event) => {
      const detail = event?.detail || {}
      const toast = {
        id: detail.id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        title: detail.title || '',
        message: detail.message || '',
        tone: detail.tone || 'info',
      }

      this.toasts = [...this.toasts, toast].slice(-4)
      const timeout = Number(detail.timeout || 3200)
      window.setTimeout(() => {
        this.dismissToast(toast.id)
      }, timeout)
    }
    window.addEventListener(APP_TOAST_EVENT, this.toastListener)

    this.serviceWorkerMessageHandler = (event) => {
      const payload = event?.data?.payload
      if (event?.data?.type !== 'DRVN_PUSH_WHILE_ACTIVE' || !payload) return

      emitAppToast({
        title: payload.title || this.$t('app.newActivity'),
        message: payload.body || this.$t('app.openDrvn'),
        tone: 'info',
        timeout: 4200,
      })
    }
    navigator.serviceWorker?.addEventListener?.('message', this.serviceWorkerMessageHandler)

    this.resumeHandler = () => {
      this.recoverAfterResume()
    }
    document.addEventListener('visibilitychange', this.resumeHandler)
    window.addEventListener('focus', this.resumeHandler)
    window.addEventListener('online', this.resumeHandler)
    window.addEventListener('pageshow', this.resumeHandler)
  },
  beforeUnmount() {
    if (this.featureFlagsListenerCleanup) {
      this.featureFlagsListenerCleanup()
      this.featureFlagsListenerCleanup = null
    }
    if (this.transientBadgeTimer) {
      clearTimeout(this.transientBadgeTimer)
      this.transientBadgeTimer = null
    }
    if (this.profileUpdateHandler) {
      window.removeEventListener('profile:updated', this.profileUpdateHandler)
      this.profileUpdateHandler = null
    }
    document.removeEventListener('click', this.handleDocumentClick)
    if (this.messageChannel) {
      supabase.removeChannel(this.messageChannel)
      this.messageChannel = null
    }
    this.teardownNotificationActivity()
    if (this.authSubscription) {
      this.authSubscription.unsubscribe()
      this.authSubscription = null
    }
    if (this.beforeInstallPromptHandler) {
      window.removeEventListener('beforeinstallprompt', this.beforeInstallPromptHandler)
      this.beforeInstallPromptHandler = null
    }
    if (this.appInstalledHandler) {
      window.removeEventListener('appinstalled', this.appInstalledHandler)
      this.appInstalledHandler = null
    }
    if (this.standaloneDisplayModeMedia && this.standaloneChangeHandler) {
      if (this.standaloneDisplayModeMedia.removeEventListener) {
        this.standaloneDisplayModeMedia.removeEventListener('change', this.standaloneChangeHandler)
      } else if (this.standaloneDisplayModeMedia.removeListener) {
        this.standaloneDisplayModeMedia.removeListener(this.standaloneChangeHandler)
      }
    }
    this.standaloneDisplayModeMedia = null
    this.standaloneChangeHandler = null
    if (this.toastListener) {
      window.removeEventListener(APP_TOAST_EVENT, this.toastListener)
      this.toastListener = null
    }
    if (this.serviceWorkerMessageHandler) {
      navigator.serviceWorker?.removeEventListener?.('message', this.serviceWorkerMessageHandler)
      this.serviceWorkerMessageHandler = null
    }
    if (this.resumeHandler) {
      document.removeEventListener('visibilitychange', this.resumeHandler)
      window.removeEventListener('focus', this.resumeHandler)
      window.removeEventListener('online', this.resumeHandler)
      window.removeEventListener('pageshow', this.resumeHandler)
      this.resumeHandler = null
    }
  },
  methods: {
    async syncDriveRadarBackgroundTracking() {
      const userId = this.session?.user?.id || ''

      if (!userId || this.featureFlags.drive_radar_enabled === false) {
        syncDriveRadarLiveLocation({ isActive: false })
        return
      }

      try {
        const nowIso = new Date().toISOString()
        const { data, error } = await supabase
          .from('drive_radar_presence')
          .select('user_id, latitude, longitude, city, country, visible_until')
          .eq('user_id', userId)
          .gt('visible_until', nowIso)
          .maybeSingle()

        if (error) return

        if (!data) {
          syncDriveRadarLiveLocation({ userId, isActive: false })
          return
        }

        syncDriveRadarLiveLocation({
          userId,
          isActive: true,
          latitude: data.latitude,
          longitude: data.longitude,
          city: data.city || '',
          country: data.country || '',
          markAsPersisted: true,
        })
      } catch (_error) {
        // Keep the current tracker state if the presence lookup fails.
      }
    },
    getSiteOrigin() {
      if (typeof window !== 'undefined' && window.location?.origin) return window.location.origin
      return 'https://drvnsocial.com'
    },
    ensureHeadTag(selector, factory) {
      if (typeof document === 'undefined') return null
      let node = document.head.querySelector(selector)
      if (!node && typeof factory === 'function') {
        node = factory()
        if (node) {
          document.head.appendChild(node)
        }
      }
      return node
    },
    setMetaTag(attr, key, content) {
      if (typeof document === 'undefined') return
      const selector = `meta[${attr}="${key}"]`
      const node = this.ensureHeadTag(selector, () => {
        const meta = document.createElement('meta')
        meta.setAttribute(attr, key)
        return meta
      })
      if (node) {
        node.setAttribute('content', content || '')
      }
    },
    setCanonical(url) {
      if (typeof document === 'undefined') return
      const node = this.ensureHeadTag('link[rel="canonical"]', () => {
        const link = document.createElement('link')
        link.setAttribute('rel', 'canonical')
        return link
      })
      if (node) {
        node.setAttribute('href', url)
      }
    },
    buildCanonicalUrl(route) {
      const origin = this.getSiteOrigin()
      const path = route?.path || '/'
      return `${origin}${path}`
    },
    getRouteSeo(route = this.$route) {
      const handle = route?.params?.handle ? String(route.params.handle) : ''
      const isPublicProfile = route?.name === 'public-profile' && handle

      const defaults = {
        title: 'DRVN - Automotive Community',
        description: 'DRVN is an automotive community for car enthusiasts, builds, meets, member profiles, and driving culture.',
        robots: 'index,follow',
        image: `${this.getSiteOrigin()}/og-image.jpg`,
      }

      if (route?.name === 'home') {
        return defaults
      }
      if (route?.name === 'about') {
        return {
          ...defaults,
          title: 'About DRVN - Automotive Community',
          description: 'Learn what DRVN is: an automotive community focused on builds, meets, member profiles, and driving culture.',
        }
      }
      if (route?.name === 'terms' || route?.name === 'privacy' || route?.name === 'cookies') {
        return {
          ...defaults,
          title: `DRVN - ${String(route.name).charAt(0).toUpperCase()}${String(route.name).slice(1)}`,
          description: 'Legal and privacy information for the DRVN automotive community platform.',
        }
      }
      if (isPublicProfile) {
        return {
          ...defaults,
          title: `@${handle} on DRVN`,
          description: `View @${handle}'s DRVN public profile, builds, and automotive community activity.`,
        }
      }

      return {
        ...defaults,
        robots: 'noindex,nofollow',
      }
    },
    applyRouteSeo() {
      if (typeof document === 'undefined') return
      const seo = this.getRouteSeo(this.$route)
      const canonicalUrl = this.buildCanonicalUrl(this.$route)

      document.title = seo.title
      this.setCanonical(canonicalUrl)
      this.setMetaTag('name', 'description', seo.description)
      this.setMetaTag('name', 'robots', seo.robots)
      this.setMetaTag('property', 'og:site_name', 'DRVN')
      this.setMetaTag('property', 'og:title', seo.title)
      this.setMetaTag('property', 'og:description', seo.description)
      this.setMetaTag('property', 'og:type', 'website')
      this.setMetaTag('property', 'og:url', canonicalUrl)
      this.setMetaTag('property', 'og:image', seo.image)
      this.setMetaTag('name', 'twitter:card', 'summary_large_image')
      this.setMetaTag('name', 'twitter:title', seo.title)
      this.setMetaTag('name', 'twitter:description', seo.description)
      this.setMetaTag('name', 'twitter:image', seo.image)
    },
    isFeaturedPhotographer(profile) {
      return isFeaturedPhotographerProfile(profile)
    },
    isRouteActive(target) {
      if (!target) return false
      if (target === '/') return this.currentRoutePath === '/'
      return this.currentRoutePath === target || this.currentRoutePath.startsWith(`${target}/`)
    },
    desktopNavLinkClass(target) {
      return [
        'inline-flex items-center gap-2 rounded-full px-3 py-2 text-[11px] font-semibold tracking-[0.18em] transition',
        this.isRouteActive(target)
          ? 'bg-white text-midnight-900 shadow-[0_8px_24px_rgba(255,255,255,0.14)]'
          : 'text-steel-300 hover:bg-white/8 hover:text-white',
      ]
    },
    desktopSidebarLinkClass(target) {
      return [
        'desktop-sidebar-link',
        this.isRouteActive(target) ? 'desktop-sidebar-link-active' : '',
      ]
    },
    mobileNavLinkClass(target) {
      return [
        'flex w-full items-center justify-between rounded-2xl border px-4 py-3 text-left transition',
        this.isRouteActive(target)
          ? 'border-white/20 bg-white text-midnight-900 shadow-[0_14px_34px_rgba(255,255,255,0.14)]'
          : 'border-white/10 bg-white/[0.03] text-steel-100 hover:border-white/20 hover:bg-white/[0.06]',
      ]
    },
    iconActionClass(target) {
      return [
        'relative inline-flex h-11 w-11 items-center justify-center rounded-2xl border transition',
        this.isRouteActive(target)
          ? 'border-white/20 bg-white text-midnight-900 shadow-[0_10px_30px_rgba(255,255,255,0.16)]'
          : 'border-white/10 bg-white/[0.03] text-steel-300 hover:border-white/20 hover:bg-white/10 hover:text-white',
      ]
    },
    mobileTabbarLinkClass(target) {
      return [
        'flex min-h-[3.5rem] flex-col items-center justify-center gap-1 rounded-[1.2rem] px-2 py-2 text-[10px] font-semibold uppercase tracking-[0.22em] transition',
        this.isRouteActive(target)
          ? 'bg-white text-midnight-900 shadow-[0_10px_26px_rgba(255,255,255,0.16)]'
          : 'text-steel-300 hover:bg-white/8 hover:text-white',
      ]
    },
    mobileTabbarButtonClass(isActive = false) {
      return [
        'flex min-h-[3.5rem] flex-col items-center justify-center gap-1 rounded-[1.2rem] px-2 py-2 text-[10px] font-semibold uppercase tracking-[0.22em] transition',
        isActive
          ? 'bg-white text-midnight-900 shadow-[0_10px_26px_rgba(255,255,255,0.16)]'
          : 'text-steel-300 hover:bg-white/8 hover:text-white',
      ]
    },
    resolveAppHref(to) {
      return this.$router.resolve(to).href
    },
    setDesktopSidebarExpanded(value) {
      this.isDesktopSidebarExpanded = value
    },
    handleDesktopSidebarFocusOut(event) {
      if (!event.currentTarget?.contains(event.relatedTarget)) {
        this.isDesktopSidebarExpanded = false
      }
    },
    navigateWithReload(to) {
      this.isDesktopSidebarExpanded = false
      this.accountMenuOpen = false
      this.isNavOpen = false
      this.searchActive = false
      this.searchQuery = ''
      this.searchResults = []

      const targetRoute = this.$router.resolve(to)
      if (targetRoute.fullPath === this.$route.fullPath) {
        // window.location.reload()
        return
      }

      // window.location.assign(targetRoute.href)
      this.$router.push(to)
    },
    async markNotificationsAsRead() {
      const userId = this.session?.user?.id
      this.unreadCount = 0
      if (this.transientBadgeTimer) {
        clearTimeout(this.transientBadgeTimer)
        this.transientBadgeTimer = null
      }
      this.showTransientBadge = false
      if (!userId) return

      try {
        await supabase
          .from('notifications')
          .update({ is_read: true })
          .eq('recipient_id', userId)
          .eq('is_read', false)
      } catch (_error) {
        // Keep the UI responsive even if marking read fails.
      }
    },
    async openNotifications() {
      await this.markNotificationsAsRead()
      this.navigateWithReload('/notifications')
    },
    openMobileSearch() {
      this.accountMenuOpen = false
      this.isNavOpen = false
      this.mobileSearchModalOpen = true
      this.searchActive = true
      this.$nextTick(() => {
        this.$refs.mobileSearchModalInput?.focus?.()
      })
    },
    closeMobileSearch() {
      this.mobileSearchModalOpen = false
      this.searchActive = false
      this.searchQuery = ''
      this.searchResults = []
      this.searching = false
    },
    openMobileComposer() {
      this.accountMenuOpen = false
      this.isNavOpen = false
      this.searchActive = false
      this.mobileSearchModalOpen = false
      this.searchQuery = ''
      this.searchResults = []
      const targetRoute = this.$router.resolve({ path: '/feed', query: { compose: '1' } })
      window.location.assign(targetRoute.href)
    },
    handleMobileImmersiveBack() {
      if (this.$route?.name === 'messages' && this.$route?.params?.handle) {
        this.navigateWithReload('/messages')
        return
      }
      if (typeof window !== 'undefined' && window.history.length > 1) {
        this.$router.back()
        return
      }
      this.navigateWithReload('/feed')
    },
    dismissToast(toastId) {
      this.toasts = this.toasts.filter((toast) => toast.id !== toastId)
    },
    async refreshPushPromptState() {
      this.pushBannerError = ''
      this.pushPermissionState = getPushPermissionState()
      this.hasGrantedPushSubscription = this.pushPermissionState === 'granted' && await hasPushSubscription()
    },
    loadThemePreference() {
      if (typeof window === 'undefined') return
      const savedTheme = window.localStorage.getItem('drvn-theme')
      this.isLightTheme = savedTheme === 'light'
      this.applyTheme()
    },
    applyTheme() {
      if (typeof document === 'undefined') return
      document.documentElement.classList.toggle('theme-light', this.isLightTheme)
      document.body.classList.toggle('theme-light', this.isLightTheme)
      this.syncThemeMeta()
    },
    syncThemeMeta() {
      if (typeof document === 'undefined') return
      const themeColor = this.isLightTheme ? '#f5f7fb' : '#0b0f14'

      let themeMeta = document.querySelector('meta[name="theme-color"]')
      if (!themeMeta) {
        themeMeta = document.createElement('meta')
        themeMeta.setAttribute('name', 'theme-color')
        document.head.appendChild(themeMeta)
      }
      themeMeta.setAttribute('content', themeColor)

      let statusBarMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]')
      if (!statusBarMeta) {
        statusBarMeta = document.createElement('meta')
        statusBarMeta.setAttribute('name', 'apple-mobile-web-app-status-bar-style')
        document.head.appendChild(statusBarMeta)
      }
      statusBarMeta.setAttribute('content', this.isLightTheme ? 'default' : 'black-translucent')
    },
    toggleTheme() {
      this.isLightTheme = !this.isLightTheme
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('drvn-theme', this.isLightTheme ? 'light' : 'dark')
      }
      this.applyTheme()
    },
    handleStandaloneChange() {
      if (this.isRunningStandalone) {
        this.installPromptEvent = null
        this.canInstallPwa = false
      }
    },
    dismissInstallBanner() {
      this.dismissedInstallBanner = true
      this.showInstallGuide = false
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('drvn-dismissed-install-banner', '1')
      }
    },
    openInstallGuide() {
      this.showInstallGuide = true
    },
    async promptInstall() {
      if (!this.installPromptEvent) {
        this.openInstallGuide()
        return
      }
      this.installPromptEvent.prompt()
      const { outcome } = await this.installPromptEvent.userChoice
      if (outcome === 'accepted') {
        this.dismissInstallBanner()
      }
      this.showInstallGuide = false
      this.installPromptEvent = null
      this.canInstallPwa = false
      window.__drvnInstallPrompt = null
    },
    async initializePushNotifications() {
      if (!this.session?.user?.id || !isPushSupported()) return
      if (Notification.permission !== 'granted') return

      try {
        await syncPushSubscription(this.session)
        await this.refreshPushPromptState()
      } catch (_error) {
        // Push registration is best-effort.
      }
    },
    dismissPushBanner() {
      this.pushPromptDismissed = true
      this.pushBannerError = ''
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('drvn-push-banner-dismissed', '1')
      }
    },
    async enablePushFromBanner() {
      if (!this.session?.user?.id) return

      this.pushPromptBusy = true
      this.pushBannerError = ''

      try {
        const session = await refreshReadySession()
        await requestPushPermissionAndSubscribe(session)
        await this.refreshPushPromptState()
        this.pushPromptDismissed = true
        if (typeof window !== 'undefined') {
          window.localStorage.setItem('drvn-push-banner-dismissed', '1')
        }
        emitAppToast({
          title: 'Push enabled',
          message: 'This device will now receive DRVN notifications.',
          tone: 'success',
        })
      } catch (error) {
        this.pushBannerError = error.message || 'Unable to enable notifications on this device.'
        await this.refreshPushPromptState()
      } finally {
        this.pushPromptBusy = false
      }
    },
    async recoverAfterResume() {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
      if (this.isRecoveringAfterResume) return

      this.isRecoveringAfterResume = true
      try {
        supabase.auth?.startAutoRefresh?.()
        supabase.realtime?.connect?.()

        this.session = await refreshReadySession()
        await this.ensureProfile(this.session)
        await Promise.all([
          this.refreshNotificationCount(),
          this.refreshMessageUnreadCount(),
          this.fetchProfile(),
        ])

        if (this.session?.user?.id) {
          this.subscribeMessageUnreadCount()
          this.subscribeNotificationActivity()
          await this.initializePushNotifications()
          await this.syncDriveRadarBackgroundTracking()
        } else {
          if (this.messageChannel) {
            supabase.removeChannel(this.messageChannel)
            this.messageChannel = null
          }
          this.teardownNotificationActivity()
          syncDriveRadarLiveLocation({ isActive: false })
        }

        window.dispatchEvent(new CustomEvent('drvn:resume', { detail: { session: this.session } }))
      } finally {
        this.isRecoveringAfterResume = false
      }
    },
    async refreshNotificationCount() {
      const userId = this.session?.user?.id
      if (!userId) {
        this.unreadCount = 0
        return
      }

      const { count } = await supabase
        .from('notifications')
        .select('id', { count: 'exact', head: true })
        .eq('recipient_id', userId)
        .eq('is_read', false)

      this.unreadCount = count || 0

      // transient pop badge: show briefly for 5s when there are unread notifications
      if (this.unreadCount > 0) {
        this.showTransientBadge = true
        if (this.transientBadgeTimer) clearTimeout(this.transientBadgeTimer)
        this.transientBadgeTimer = setTimeout(() => {
          this.showTransientBadge = false
          this.transientBadgeTimer = null
        }, 5000)
      } else {
        if (this.transientBadgeTimer) {
          clearTimeout(this.transientBadgeTimer)
          this.transientBadgeTimer = null
        }
        this.showTransientBadge = false
      }
    },
    async refreshMessageUnreadCount() {
      const userId = this.session?.user?.id
      if (!userId) {
        this.messageUnreadCount = 0
        return
      }

      const { count } = await supabase
        .from('direct_messages')
        .select('id', { count: 'exact', head: true })
        .eq('recipient_id', userId)
        .is('seen_at', null)

      this.messageUnreadCount = count || 0
    },
    subscribeMessageUnreadCount() {
      if (!this.session?.user?.id) return
      if (this.messageChannel) {
        supabase.removeChannel(this.messageChannel)
      }

      this.messageChannel = supabase
        .channel(`app-direct-messages-${this.session.user.id}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'direct_messages' }, async () => {
          await this.refreshMessageUnreadCount()
        })
        .subscribe()
    },
    teardownNotificationActivity() {
      if (this.notificationChannel) {
        supabase.removeChannel(this.notificationChannel)
        this.notificationChannel = null
      }
    },
    subscribeNotificationActivity() {
      const userId = this.session?.user?.id
      if (!userId) {
        this.teardownNotificationActivity()
        return
      }

      this.teardownNotificationActivity()
      this.notificationChannel = supabase
        .channel(`app-notifications-${userId}`)
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `recipient_id=eq.${userId}`,
        }, async (payload) => {
          await this.refreshNotificationCount()
          await this.showIncomingNotificationToast(payload?.new)
        })
        .subscribe()
    },
    getNotificationToastMeta(type = '', actor = {}) {
      const actorName = actor.full_name || actor.handle || 'Someone'
      const actorHandle = actor.handle ? `@${actor.handle}` : actorName

      if (type === 'post_like') return { title: 'New like', message: `${actorHandle} liked your post.`, tone: 'success' }
      if (type === 'post_save') return { title: 'Post saved', message: `${actorHandle} saved your build.`, tone: 'success' }
      if (type === 'post_comment') return { title: 'New comment', message: `${actorHandle} commented on your post.`, tone: 'info' }
      if (type === 'comment_reply') return { title: 'New reply', message: `${actorHandle} replied to your comment.`, tone: 'info' }
      if (type === 'comment_like') return { title: 'Comment liked', message: `${actorHandle} liked your comment.`, tone: 'success' }
      if (type === 'follow') return { title: 'New follower', message: `${actorName} started following you.`, tone: 'success' }
      if (String(type).startsWith('post_reaction_')) {
        const reactionTone = String(type).replace('post_reaction_', '').replaceAll('_', ' ')
        return { title: 'New reaction', message: `${actorHandle} reacted ${reactionTone} to your post.`, tone: 'info' }
      }

      return { title: 'New activity', message: `${actorHandle} interacted with you.`, tone: 'info' }
    },
    async showIncomingNotificationToast(notification) {
      if (!notification?.actor_id) return

      const { data: actor } = await supabase
        .from('profiles')
        .select('handle, full_name')
        .eq('id', notification.actor_id)
        .maybeSingle()

      const toast = this.getNotificationToastMeta(notification.type, actor || {})
      emitAppToast({ ...toast, timeout: 4200 })
    },
    async fetchProfile() {
      const userId = this.session?.user?.id
      if (!userId) {
        this.profile = {}
        return
      }
      const { data, error } = await runSupabaseQueryWithRetry(() => (
        supabase
          .from('profiles')
          .select('handle, full_name, avatar_url, avatar_path, role, status')
          .eq('id', userId)
          .maybeSingle()
      ))
      if (!error && data) {
        this.profile = data
        if (data.status === 'banned') {
          await supabase.auth.signOut()
          this.session = null
          this.$router.push('/login?banned=1')
        }
      }
    },

    async ensureProfile(session) {
      try {
        const user = session?.user
        if (!user) return

        const userId = user.id
        // check if profile exists
        const { data: existing } = await supabase
          .from('profiles')
          .select('id, avatar_url')
          .eq('id', userId)
          .maybeSingle()

        // derive fields from user metadata or fallback to email prefix / id
        const meta = user.user_metadata || {}
        const handleFromMeta = (meta.handle && String(meta.handle).toLowerCase()) || ''
        const emailHandle = user.email ? String(user.email).split('@')[0].toLowerCase() : ''
        const handle = handleFromMeta || emailHandle || userId.slice(0, 8)
        const full_name = meta.full_name || meta.name || ''
        const avatar_url = meta.avatar_url || meta.picture || ''

        if (existing) {
          if (avatar_url && !existing.avatar_url) {
            await supabase.from('profiles').update({ avatar_url }).eq('id', userId)
          }
          return
        }

        await supabase
          .from('profiles')
          .upsert(
            {
              id: userId,
              handle,
              full_name,
              avatar_url,
            },
            { onConflict: 'id' }
          )
      } catch (e) {
        // ignore - profile creation is best-effort here
        console.warn('ensureProfile failed', e)
      }
    },
    handleDocumentClick(event) {
      const target = event?.target
      if (!(target instanceof Element)) return

      if (!target.closest('[data-account-menu-root]')) {
        this.accountMenuOpen = false
      }

      if (!this.mobileSearchModalOpen && !target.closest('[data-search-root]')) {
        this.searchActive = false
      }
    },
    // close the account menu on route change
    watchRoute() {
      this.isDesktopSidebarExpanded = false
      this.accountMenuOpen = false
    },

    async signOut() {
      await supabase.auth.signOut()
      this.session = null
      this.profile = {}
      this.hasGrantedPushSubscription = false
      this.pushPermissionState = getPushPermissionState()
      this.accountMenuOpen = false
      this.isNavOpen = false
      await this.$router.replace('/login')
    },
    handleSearch() {
      if (!this.searchQuery) return
      if (this.searchResults.length) {
        this.goToUser(this.searchResults[0].handle)
        return
      }
      const handle = this.searchQuery.replace(/^@/, '')
      this.$router.push(`/u/${handle}`)
      this.searchQuery = ''
      this.searchActive = false
      this.mobileSearchModalOpen = false
    },
    queueSearch() {
      if (this.searchTimer) {
        clearTimeout(this.searchTimer)
      }
      const query = this.searchQuery
      if (!query) {
        this.searchResults = []
        this.searching = false
        this.searchActive = false
        return
      }
      this.searchActive = true
      this.searching = true
      this.searchTimer = setTimeout(() => {
        this.fetchSearchResults(query)
      }, 250)
    },
    async fetchSearchResults(query) {
      const cleaned = query.replace(/^@/, '')
      const { data, error } = await supabase
        .from('profiles')
        .select('handle, full_name, avatar_url, avatar_path, verified')
        .or(`handle.ilike.%${cleaned}%,full_name.ilike.%${cleaned}%`)
        .limit(5)

      if (error) {
        this.searchResults = []
        this.searching = false
        return
      }

      this.searchResults = (data || []).map((user) => {
        const avatar = getAvatarUrl({ avatarPath: user.avatar_path, avatarUrl: user.avatar_url })
        return {
          handle: user.handle,
          fullName: user.full_name,
          avatar,
          verified: Boolean(user.verified),
        }
      })
      this.searching = false
    },
    goToUser(handle) {
      this.$router.push(`/u/${handle}`)
      this.searchQuery = ''
      this.searchResults = []
      this.searching = false
      this.searchActive = false
      this.mobileSearchModalOpen = false
    },
  },
}
</script>

<style scoped>
.desktop-shell-sidebar {
  position: fixed;
  inset: 0 auto 0 0;
  z-index: 140;
  width: 6.5rem;
  padding: 1rem 0 1rem 1rem;
  pointer-events: none;
}

.desktop-shell-rail {
  pointer-events: auto;
  display: flex;
  height: 100%;
  width: 5.25rem;
  flex-direction: column;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 2rem;
  background: linear-gradient(180deg, rgba(12, 16, 24, 0.98), rgba(7, 10, 16, 0.98));
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.35);
  overflow: visible;
  transition: width 180ms ease, border-color 180ms ease, background 180ms ease;
}

.desktop-shell-sidebar-expanded .desktop-shell-rail {
  width: 16rem;
  border-color: rgba(255, 255, 255, 0.14);
}

.desktop-sidebar-label {
  max-width: 0;
  opacity: 0;
  overflow: hidden;
  white-space: nowrap;
  transition: max-width 180ms ease, opacity 140ms ease;
}

.desktop-sidebar-expand {
  max-height: 0;
  opacity: 0;
  overflow: hidden;
  transition: max-height 220ms ease, opacity 140ms ease;
}

.desktop-shell-sidebar-expanded .desktop-sidebar-label {
  max-width: 12rem;
  opacity: 1;
}

.desktop-shell-sidebar-expanded .desktop-sidebar-expand {
  max-height: 12rem;
  opacity: 1;
  overflow: visible;
}

.desktop-sidebar-link {
  display: flex;
  min-height: 3.35rem;
  align-items: center;
  justify-content: center;
  gap: 0;
  border-radius: 1.35rem;
  padding: 0.8rem 0.95rem;
  color: #94a3b8;
  transition: background-color 160ms ease, color 160ms ease, border-color 160ms ease, box-shadow 160ms ease;
}

.desktop-shell-sidebar-expanded .desktop-sidebar-link {
  justify-content: flex-start;
  gap: 0.9rem;
}

.desktop-sidebar-link:hover {
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
}

.desktop-sidebar-link-active {
  background: #fff;
  color: #0b0f14;
  box-shadow: 0 12px 28px rgba(255, 255, 255, 0.14);
}

@media (min-width: 951px) and (max-height: 760px) {
  .desktop-shell-sidebar {
    padding-top: 0.65rem;
    padding-bottom: 0.65rem;
  }

  .desktop-shell-rail {
    border-radius: 1.65rem;
  }

  .desktop-shell-rail > div:first-child {
    padding-top: 0.65rem;
  }

  .desktop-shell-rail > nav {
    margin-top: 0.55rem;
    gap: 0.15rem;
  }

  .desktop-shell-rail > div:last-child {
    gap: 0.15rem;
    padding-bottom: 0.65rem;
  }

  .desktop-sidebar-expand {
    padding-top: 0.55rem;
  }

  .desktop-sidebar-link {
    min-height: 2.7rem;
    border-radius: 1rem;
    padding-top: 0.55rem;
    padding-bottom: 0.55rem;
  }
}

@media (min-width: 951px) and (max-height: 560px) {
  .desktop-shell-sidebar {
    padding-top: 0.4rem;
    padding-bottom: 0.4rem;
  }

  .desktop-shell-rail > div:first-child {
    padding-top: 0.4rem;
  }

  .desktop-shell-rail > nav {
    margin-top: 0.35rem;
    gap: 0;
  }

  .desktop-shell-rail > div:last-child {
    gap: 0;
    padding-bottom: 0.4rem;
  }

  .desktop-sidebar-link {
    min-height: 2.35rem;
    padding-top: 0.35rem;
    padding-bottom: 0.35rem;
  }
}

.mobile-nav-scroll {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.mobile-nav-scroll::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}

.pop-animate {
  animation: pop 0.8s ease-in-out infinite;
  transform-origin: center;
}
@keyframes pop {
  0% { transform: scale(1); }
  50% { transform: scale(1.25); }
  100% { transform: scale(1); }
}
</style>

<style>
button,
a,
[role="button"],
input,
select,
textarea {
  -webkit-tap-highlight-color: transparent;
}

.app-pressable {
  transition: transform 150ms ease, opacity 150ms ease, background-color 150ms ease, border-color 150ms ease, box-shadow 150ms ease;
  will-change: transform;
}

.app-pressable:active {
  transform: scale(0.975);
}

.app-card-pressable {
  transition: transform 180ms ease, box-shadow 180ms ease, background-color 180ms ease, border-color 180ms ease;
  will-change: transform;
}

.app-card-pressable:active {
  transform: scale(0.988);
}

.app-skeleton {
  position: relative;
  overflow: hidden;
}

.app-skeleton::after {
  content: '';
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.08), transparent);
  animation: app-skeleton-shimmer 1.35s ease-in-out infinite;
}

.app-badge-pulse {
  animation: app-badge-pulse 2s ease-in-out infinite;
}

@keyframes app-skeleton-shimmer {
  100% {
    transform: translateX(100%);
  }
}

@keyframes app-badge-pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.08);
  }
}

html,
body,
* {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

html::-webkit-scrollbar,
body::-webkit-scrollbar,
*::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}
</style>
